import { attachment } from '@ibinder/common-icons';
import {
  FormTypeValueEnum,
  MetadataTypeEnum,
  ResultColorOptions,
  ResultSettings,
  ResultSettingsKeyword,
  ResultTypeEnum
} from '@ibinder/ui-common-models';
import { MetadataElementIdConstants } from '@ibinder/common-utils';
import { IssueFormSection } from '@ibinder/ui-common-forms';
import { InspectionLabels } from './translationHelpers';
import { KeyValue } from '@ibinder/ui-common-core';
import { SingleRenderSelectedSearch } from '@ibinder/ui-common-core';

export enum ColorOptions {
  Grey = 'Grey',
  Red = 'Red',
  Yellow = 'Yellow',
  Green = 'Green',
  Blue = 'Blue',
  Purple = 'Purple'
}
export const noTemplate = "noTemplate";

interface WorkflowTemplateData {
  workflowTemplateList?: KeyValue[];
  canUseInspectionIssuesWithWorkflow: boolean;
}
export interface InspectionTemplate {
  id?: string;
  name: string;
  description: string;
  market: string;
  inspectionCategory: string;
  disabled?: boolean;
  appCompatible?: boolean;
  propertyStructureCompatible?: boolean;
  showRecurrenceOptions?: boolean;
  showReminderOptions?: boolean;
  inspectionInterval: {
    type: string;
    interval: string;
    unit: string;
  };
  setDefaultContractor: boolean;
  defaultContractor: string;
  contractor: {
    type: string;
    selectedContractor: string;
  };
  appType: string[];
  inspectionPlanResultSettings: ResultSettings;
  inspectionOccasionResultSettings: ResultSettings;
  remindBeforeDueDate: string;
  supervisorRole: any;
  leniencyDocuments?: any[];
  additionalDocuments?: any[];
  workflowTemplateData?: {
    workflowTemplateList: KeyValue[];
    canUseInspectionIssuesWithWorkflow: boolean;
  };
  workflowTemplateId?: string;
  uniqueNumberCountOnOccasionLevel?: boolean;
}

const FirstSection = (
  editMode: boolean,
  inspectionTemplate: InspectionTemplate,
  labels: InspectionLabels,
  marketOptions: { Key: string; Value: string }[],
  categoryOptions: { Key: string; Value: string }[],
  recurrenceUnitOptions: { Key: string; Value: string }[],
  contractorsOptions: { Key: string; Value: string }[],
  workflowTemplateData: WorkflowTemplateData,
  shouldShowIssueNumbering: boolean
): IssueFormSection<InspectionTemplate> => {
  return [
    [
      {
        inputType: 'text',
        icon: null,
        label: labels.nameLabel,
        direction: 'row',
        prop: 'name',
        placeholder: labels.enter,
        required: true,
        templateId: 'name',
        helpMessages: {
          isRequired: labels.cannotBeEmpty
        }
      }
    ],
    [
      {
        inputType: 'textarea',
        label: labels.descriptionLabel,
        direction: 'row',
        prop: 'description',
        placeholder: labels.enter,
        templateId: 'description',
        helpMessages: {
          isRequired: labels.cannotBeEmpty
        }
      }
    ],
    [
      {
        inputType: 'select',
        disabled: editMode,
        data: marketOptions,
        label: labels.marketLabel,
        multiSelect: false,
        direction: 'row',
        prop: 'market',
        placeholder: labels.marketPlaceholder,
        templateId: 'market',
        helpMessages: {
          isRequired: labels.cannotBeEmpty
        }
      }
    ],
    [
      ...(inspectionTemplate.market || inspectionTemplate.market === null
        ? [
            {
              inputType: 'selectWithCreateOptions',
              data: categoryOptions,
              label: labels.categoryLabel,
              direction: 'row',
              prop: 'inspectionCategory',
              placeholder: labels.categoryPlaceholder,
              templateId: 'inspectionCategory',
              helpMessages: {
                isRequired: labels.cannotBeEmpty
              }
            }
          ]
        : [])
    ],
    [
      {
        inputType: 'intervalComposite',
        label: labels.intervalLabel,
        direction: 'row',
        prop: 'inspectionInterval',
        placeholder: '',
        hint: labels.inspectionIntervalHint,
        subInputs: [
          {
            inputType: 'radio',
            label: '',
            direction: 'row',
            prop: 'type',
            placeholder: '',
            data: [
              { Value: labels.intervalOption1, Key: 'noRecurrentInterval' },
              { Value: labels.intervalOption2, Key: 'suggestedInterval' }
            ],
            defaultValue: 'suggestedInterval',
            enableShowMoreOptionsFor: 'suggestedInterval',
            onChange: ({ key, onChange }) => {
              const suggestedInterval = 'suggestedInterval';
              if (key === suggestedInterval) {
                onChange({
                  type: suggestedInterval,
                  interval: '1',
                  unit: 'M'
                });
              }
            }
          },
          {
            inputType: 'text',
            icon: null,
            label: labels.intervalSubInput1,
            direction: 'row',
            prop: 'interval',
            placeholder: ''
          },
          {
            inputType: 'select',
            data: recurrenceUnitOptions,
            label: '',
            direction: 'row',
            prop: 'unit',
            placeholder: labels.intervalSubInput2Placeholder
          }
        ]
      }
    ],
    [
      {
        inputType: 'intervalComposite',
        label: labels.contractorLabel,
        direction: 'row',
        prop: 'contractor',
        placeholder: '',
        subInputs: [
          {
            inputType: 'radio',
            label: '',
            direction: 'row',
            prop: 'type',
            placeholder: '',
            data: [
              { Value: labels.contractorOption1, Key: 'noDefault' },
              { Value: labels.contractorOption2, Key: 'setDefault' }
            ],
            defaultValue: 'noDefault',
            enableShowMoreOptionsFor: 'setDefault',
            onChange: ({ key, onChange }) => {
              const setDefault = 'setDefault';
              if (key === setDefault) {
                onChange({
                  type: setDefault,
                  selectedContractor: contractorsOptions[0].Key
                });
              }
            }
          },

          {
            inputType: 'select',
            data: contractorsOptions,
            label: '',
            direction: 'row',
            prop: 'selectedContractor',
            defaultValue: '',
            placeholder: labels.intervalSubInput2Placeholder
          }
        ]
      }
    ],
    [
      {
        inputType: 'checkbox',
        label: labels.otherSettingsLabel,
        direction: 'row',
        prop: 'appType',
        data: [
          { Value: labels.otherSettingsOption1, Key: 'property' },
          { Value: labels.otherSettingsOption2, Key: 'app', selectAll: true }
        ]
      }
    ],
    [
      {
        inputType: 'resultSettings',
        icon: null,
        direction: 'column',
        prop: ResultSettingsKeyword.inspectionPlanResultSettings,
        label: labels.inspectionPlanResultSettings,
        hint: labels.inspectionResultSettingsHint,
        subInputs: [
          {
            inputType: 'radio',
            label: labels.resultRadioLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.resultType,
            data: [
              {
                Value: labels.resultRadioOption0,
                Key: ResultTypeEnum.NoResult
              },
              {
                Value: labels.resultRadioOption4,
                Key: ResultTypeEnum.MultipleChoice
              }
            ],
            defaultValue: ResultTypeEnum.NoResult
          },

          {
            inputType: 'text',
            label: labels.settingsLabelForSingleSelect,
            direction: 'row',
            prop: ResultSettingsKeyword.resultChoiceName,
            defaultValue: '',
            placeholder: labels.enter
          },
          {
            inputType: 'select',
            data: ResultColorOptions,
            label: '',
            direction: 'row',
            prop: ResultSettingsKeyword.resultChoiceColor,
            defaultValue: '#6b7280',
            placeholder: labels.enterOrSelect
          },
          {
            inputType: 'button',
            label: labels.addAlternativeButtonLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.addAlternative,
            defaultValue: ''
          }
        ]
      }
    ],

    [
      {
        inputType: 'resultSettings',
        icon: null,
        direction: 'column',
        prop: ResultSettingsKeyword.inspectionOccasionResultSettings,
        label: labels.resultSettingsLabel,
        subInputs: [
          {
            inputType: 'radio',
            label: labels.resultRadioLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.resultType,
            data: [
              {
                Value: labels.resultRadioOption1,
                Key: ResultTypeEnum.MultipleChoice
              },
              {
                Value: labels.resultRadioOption2,
                Key: ResultTypeEnum.TextWithUnit
              }
            ],
            defaultValue: ResultTypeEnum.MultipleChoice
          },

          {
            inputType: 'text',
            label: labels.settingsLabelForSingleSelect,
            direction: 'row',
            prop: ResultSettingsKeyword.resultChoiceName,
            defaultValue: '',
            placeholder: labels.enter
          },
          {
            inputType: 'select',
            data: ResultColorOptions,
            label: '',
            direction: 'row',
            prop: ResultSettingsKeyword.resultChoiceColor,
            defaultValue: '#6b7280',
            placeholder: labels.enterOrSelect
          },
          {
            inputType: 'button',
            label: labels.addAlternativeButtonLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.addAlternative,
            defaultValue: ''
          },

          {
            inputType: 'select',
            data: ResultColorOptions,
            label: labels.colorLessThanLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.unitTypeInterpretationAboveValueColor,
            defaultValue: '#6b7280',
            placeholder: labels.enterOrSelect
          },
          {
            inputType: 'number',
            label: labels.Interpretation,
            direction: 'row',
            prop: ResultSettingsKeyword.unitTypeInterpretationThresholdValue,
            defaultValue: '',
            placeholder: labels.enter
          },
          {
            inputType: 'select',
            data: ResultColorOptions,
            label: labels.colorGreaterThanLabel,
            direction: 'row',
            prop: ResultSettingsKeyword.unitTypeInterpretationBelowValueColor,
            defaultValue: '#6b7280',
            placeholder: labels.enterOrSelect
          },
          {
            inputType: 'text',
            label: labels.unit,
            direction: 'row',
            prop: ResultSettingsKeyword.unitTypeMetric,
            defaultValue: '',
            placeholder: labels.enter
          }
        ]
      }
    ],
    workflowTemplateData.canUseInspectionIssuesWithWorkflow
      ? [
          {
            inputType: 'singleSelectWithSearch',
            label: labels.template,
            subHeading: labels.caseManagementTemplate,
            direction: 'row',
            prop: 'workflowTemplateId',
            placeholder: labels.enterOrSelect,
            data: workflowTemplateData?.workflowTemplateList,
            showTopDivider: true,
            showBottomDivider: true,
            groupByHeader: true,
            showChevron: true,
            groupByHeaderOptions: [
              {
                tenantSpecific: 'noTemplate',
                label: labels.template
              },
              {
                tenantSpecific: false,
                label: labels.globaltemplates
              }
            ],
            onChange: (value, setValue) => {
              if (!value || value === noTemplate) {
                setValue('uniqueNumberCountOnOccasionLevel', false);
              }
            },
            renderSelected: (value) =>
              SingleRenderSelectedSearch(
                value,
                workflowTemplateData?.workflowTemplateList,
                false,
                true,
                true
              )
          }
        ]
      : [],
    shouldShowIssueNumbering
      ? [
          {
            inputType: "select",
            label: labels.issueNumbering,
            direction: "row",
            prop: 'uniqueNumberCountOnOccasionLevel',
            data: [
              { Key: true, Value: labels.inspectionSeparateIssueCount },
              { Key: false, Value: labels.inspectionWholeIssueCount },
            ],
            defaultValue: false.toString(),
            hint: labels.issueNumberingHint,
          },
        ] 
      : [],
    [
      {
        inputType: 'files',
        icon: attachment,
        label: labels.inspectionTemplateDoc,
        direction: 'column',
        prop: 'leniencyDocuments',
        templateId: MetadataElementIdConstants.GLOBAL_IssueTitle,
        placeholder: labels.enterOrselect,
        multiple: true,
        filesLabels: {
          linkFromProjectLabel: '',
          dragAndDropLabel: labels.dragAndDropLabel,
          browse: labels.browseLabel,
          uploadLabel: labels.upload
        }
      }
    ],
    [
      {
        inputType: 'files',
        icon: attachment,
        label: labels.additionalDocumentsLabel,
        direction: 'column',
        prop: 'additionalDocuments',
        templateId: MetadataElementIdConstants.GLOBAL_IssueTitle,
        placeholder: labels.enterOrselect,
        multiple: true,
        filesLabels: {
          linkFromProjectLabel: '',
          dragAndDropLabel: labels.dragAndDropLabel,
          browse: labels.browseLabel,
          uploadLabel: labels.upload
        }
      }
    ]
  ];
};

interface IssueFormSectionsParams {
  editMode: boolean;
  inspectionTemplate: InspectionTemplate;
  labels: InspectionLabels;
  marketOptions: { Key: string; Value: string }[];
  categoryOptions: { Key: string; Value: string }[];
  recurrenceUnitOptions: { Key: string; Value: string }[];
  contractorsOptions: { Key: string; Value: string }[];
  workflowTemplateData: {
    workflowTemplateList: { Key: string; Value: string }[];
    canUseInspectionIssuesWithWorkflow: boolean;
  };
  shouldShowIssueNumbering: boolean;
}

export const issueFormSections = ({
  editMode,
  inspectionTemplate,
  labels,
  marketOptions,
  categoryOptions,
  recurrenceUnitOptions,
  contractorsOptions,
  workflowTemplateData,
  shouldShowIssueNumbering
}: IssueFormSectionsParams): IssueFormSection<InspectionTemplate>[] => [
  FirstSection(
    editMode,
    inspectionTemplate,
    labels,
    marketOptions,
    categoryOptions,
    recurrenceUnitOptions,
    contractorsOptions,
    workflowTemplateData,
    shouldShowIssueNumbering
  )
];

//This helps for a better understanding of the structure of the form
//required field is enabled for some fields
//TODO: Need to check how to implement it in better way
export const InspectionTemplateFormRenderingStructure = {
  id: 'template',
  name: null,
  items: [
    {
      id: 'name',
      itemMetadata: {
        id: 'name',
        name: 'name',
        tenantSpecific: false,
        typeParameters: {},
        optionSettings: {
          enabled: false,
          options: []
        },
        readonly: false,

        type: MetadataTypeEnum.Text
      },
      formType: {
        value: FormTypeValueEnum.TextForm,
        formLabel: '',
        multiple: false
      },
      required: true
    },
    {
      id: 'description',
      itemMetadata: {
        id: 'description',
        name: 'description',
        tenantSpecific: false,
        typeParameters: {},
        optionSettings: {
          enabled: false,
          options: []
        },
        readonly: false,

        type: MetadataTypeEnum.Text
      },
      formType: {
        value: FormTypeValueEnum.TextForm,
        formLabel: '',
        multiple: false
      },
      required: true
    },
    {
      id: 'market',
      itemMetadata: {
        id: 'market',
        name: 'market',
        tenantSpecific: false,
        typeParameters: {},
        optionSettings: {
          enabled: false,
          options: []
        },
        readonly: false,

        type: MetadataTypeEnum.Options
      },
      formType: {
        value: FormTypeValueEnum.Option,
        formLabel: '',
        multiple: false
      },
      required: true
    },
    {
      id: 'inspectionCategory',
      itemMetadata: {
        id: 'inspectionCategory',
        name: 'inspectionCategory',
        tenantSpecific: false,
        typeParameters: {},
        optionSettings: {
          enabled: false,
          options: []
        },
        readonly: false,

        type: MetadataTypeEnum.Options
      },
      formType: {
        value: FormTypeValueEnum.Option,
        formLabel: '',
        multiple: false
      },
      required: true
    }
  ],
  tenantSpecific: false
};
